/**
 * Knowledge Base Upload Component
 * Component for uploading, viewing, and deleting knowledge base files for Custom GPT
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Upload, X, FileText, File, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { uploadKnowledgeBaseFile, deleteKnowledgeBaseFile } from '../../lib/api/customGPT'
import type { CustomGPTFile } from '../../types/custom-gpt'
import { formatFileSize, isValidFileType, isValidFileSize, formatFileDate } from '../../lib/fileUtils'
import { cn } from '../ui/utils'

interface KnowledgeBaseUploadProps {
  gptId: string | null // null when creating (will be set after creation)
  existingFiles?: CustomGPTFile[]
  onFilesChange?: (files: CustomGPTFile[]) => void
  disabled?: boolean
}

export function KnowledgeBaseUpload({
  gptId,
  existingFiles = [],
  onFilesChange,
  disabled = false,
}: KnowledgeBaseUploadProps) {
  const [files, setFiles] = useState<CustomGPTFile[]>(existingFiles)
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState<string[]>([]) // Track files being uploaded by name
  const [deleting, setDeleting] = useState<string[]>([]) // Track files being deleted by ID
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Update files when existingFiles prop changes
  useEffect(() => {
    setFiles(existingFiles)
  }, [existingFiles])

  const handleFileSelect = useCallback(
    async (selectedFiles: FileList | File[]) => {
      if (!gptId) {
        toast.error('Please save the GPT first before uploading files')
        return
      }

      const fileArray = Array.from(selectedFiles)
      const validFiles: File[] = []
      const errors: string[] = []

      // Validate files
      for (const file of fileArray) {
        if (!isValidFileType(file)) {
          errors.push(`${file.name}: Invalid file type. Allowed: PDF, DOCX, Excel (.xlsx, .xls), TXT`)
          continue
        }

        if (!isValidFileSize(file)) {
          errors.push(`${file.name}: File too large. Max size: 30MB`)
          continue
        }

        validFiles.push(file)
      }

      // Show errors
      if (errors.length > 0) {
        errors.forEach((error) => toast.error(error))
      }

      if (validFiles.length === 0) {
        return
      }

      // Upload valid files
      for (const file of validFiles) {
        try {
          setUploading((prev) => [...prev, file.name])
          const uploadedFile = await uploadKnowledgeBaseFile(gptId, file)
          
          const newFile: CustomGPTFile = {
            id: uploadedFile.id,
            file_name: uploadedFile.file_name,
            file_size: uploadedFile.file_size,
            file_type: uploadedFile.file_type,
            uploaded_at: new Date().toISOString(),
          }

          setFiles((prev) => {
            const updated = [...prev, newFile]
            onFilesChange?.(updated)
            return updated
          })
          toast.success(`Uploaded: ${file.name}`)
        } catch (error) {
          toast.error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
        } finally {
          setUploading((prev) => prev.filter((name) => name !== file.name))
        }
      }
    },
    [gptId, onFilesChange]
  )

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files)
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && gptId) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled || !gptId) {
      return
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files)
    }
  }

  const handleDelete = async (fileId: string, fileName: string) => {
    if (!gptId) {
      toast.error('Unable to delete file')
      return
    }

    try {
      setDeleting((prev) => [...prev, fileId])
      await deleteKnowledgeBaseFile(gptId, fileId)
      setFiles((prev) => {
        const updated = prev.filter((f) => f.id !== fileId)
        onFilesChange?.(updated)
        return updated
      })
      toast.success(`Deleted: ${fileName}`)
    } catch (error) {
      toast.error(`Failed to delete ${fileName}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setDeleting((prev) => prev.filter((id) => id !== fileId))
    }
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
    return <FileText className="w-4 h-4" />
  }

  const canUpload = gptId && !disabled

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Knowledge Base Files</Label>
        {files.length > 0 && (
          <Badge variant="secondary">{files.length} file{files.length !== 1 ? 's' : ''}</Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Upload files (PDF, DOCX, Excel, TXT) to provide context for your Custom GPT. Max 30MB per file.
      </p>

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
          isDragging && canUpload
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          disabled || !canUpload ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        )}
        onClick={() => {
          if (canUpload && fileInputRef.current) {
            fileInputRef.current.click()
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.xlsx,.xls,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/plain"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled || !canUpload}
        />
        <div className="flex flex-col items-center gap-2">
          <Upload className={cn('w-8 h-8', isDragging && canUpload ? 'text-primary' : 'text-muted-foreground')} />
          <div>
            <p className="text-sm font-medium">
              {!canUpload
                ? 'Save the GPT first to upload files'
                : isDragging
                  ? 'Drop files here'
                  : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              PDF, DOCX, Excel (.xlsx, .xls), TXT up to 30MB
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2 border rounded-lg p-4">
          {files.map((file) => {
            const isUploading = uploading.includes(file.file_name)
            const isDeleting = deleting.includes(file.id)

            return (
              <div
                key={file.id}
                className="flex items-center justify-between gap-4 p-3 rounded-md bg-muted/50 hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {getFileIcon(file.file_name)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.file_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">{formatFileSize(file.file_size)}</span>
                      {file.uploaded_at && (
                        <>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">{formatFileDate(file.uploaded_at)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete(file.id, file.file_name)
                  }}
                  disabled={isDeleting || isUploading || disabled || !canUpload}
                  className="shrink-0"
                >
                  {isDeleting || isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 text-destructive" />
                  )}
                </Button>
              </div>
            )
          })}
        </div>
      )}

      {/* Upload Progress */}
      {uploading.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Uploading: {uploading.join(', ')}
        </div>
      )}
    </div>
  )
}

